import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  inject,
  Injectable,
  InjectionToken,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { filter, fromEvent } from 'rxjs';
import { BUILD_VERSION } from '../../environments/build-version';

/** The commit this bundle was built from; null turns the check off. */
export const APP_BUILD_VERSION = new InjectionToken<string | null>(
  'APP_BUILD_VERSION',
  { providedIn: 'root', factory: () => BUILD_VERSION }
);

/** Loads a router URL as a fresh document, so the server's current build runs it. */
export const PAGE_RELOAD = new InjectionToken<(routerUrl: string) => void>(
  'PAGE_RELOAD',
  {
    providedIn: 'root',
    factory: () => {
      const document = inject(DOCUMENT);
      return (routerUrl: string) => {
        // The app routes with withHashLocation(), and a hash-only change is a
        // same-document navigation: put the target in the address bar first,
        // then reload it. A back/forward navigation already has it there.
        const hash = `#${routerUrl}`;
        if (document.location.hash !== hash) {
          document.defaultView?.history.pushState(null, '', hash);
        }
        document.location.reload();
      };
    },
  }
);

const CHECK_INTERVAL_MS = 5 * 60_000;
const RELOAD_ATTEMPT_KEY = 'domaingrc.app-version.reload';
// Chrome, Firefox and Safari word a failed dynamic import differently.
const CHUNK_LOAD_FAILURE =
  /dynamically imported module|Importing a module script failed/i;

/**
 * Keeps an open tab from running a build the server no longer serves.
 *
 * A tab keeps the JavaScript it loaded for as long as it stays open, while the
 * API it talks to and the lazy chunks it has not fetched yet move on with every
 * deploy. An old bundle reading a new response shape crashes
 * (DOMAINGRC-WEB-38), and one asking for a chunk the deploy deleted fails to
 * navigate (DOMAINGRC-WEB-Q).
 *
 * After a navigation, and when the tab comes back into view, at most once per
 * {@link CHECK_INTERVAL_MS}, it compares the deploy's `__version.json` with the
 * version built into this bundle. Once they differ, the next navigation loads
 * its target as a full page instead of switching views in place, so nothing on
 * the screen the user is working in is thrown away.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly buildVersion = inject(APP_BUILD_VERSION);
  private readonly reloadPage = inject(PAGE_RELOAD);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** The server's version once it is known to differ from this bundle's. */
  private serverVersion: string | null = null;
  private lastCheckAt: number | null = null;
  private checking = false;

  start(): void {
    if (!this.isBrowser || !this.buildVersion) {
      return;
    }
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (this.serverVersion) {
            this.reloadOnce(`version:${this.serverVersion}`, event.url);
          }
        } else if (event instanceof NavigationEnd) {
          void this.check();
        } else if (
          event instanceof NavigationError &&
          isChunkLoadFailure(event.error)
        ) {
          this.reloadOnce(`chunk:${this.buildVersion}`, event.url);
        }
      });
    fromEvent(this.document, 'visibilitychange')
      .pipe(
        filter(() => this.document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => void this.check());
  }

  private async check(): Promise<void> {
    const now = Date.now();
    if (
      this.serverVersion ||
      this.checking ||
      (this.lastCheckAt !== null && now - this.lastCheckAt < CHECK_INTERVAL_MS)
    ) {
      return;
    }
    this.checking = true;
    this.lastCheckAt = now;
    try {
      const response = await fetch(
        new URL('__version.json', this.document.baseURI),
        { cache: 'no-store' }
      );
      if (!response.ok) {
        return;
      }
      const version = readVersion(await response.json());
      if (version && version !== this.buildVersion) {
        this.serverVersion = version;
      }
    } catch (error) {
      // Offline, or the file is mid-upload: the next navigation or focus
      // after the interval asks again.
      console.warn('Checking for a new app version failed', error);
    } finally {
      this.checking = false;
    }
  }

  /**
   * One reload per reason per tab. If the reload brings back the same bundle
   * (the CDN still serving the old index.html, a chunk that is really gone),
   * the tab carries on rather than reloading on every click.
   */
  private reloadOnce(reason: string, routerUrl: string): void {
    if (readReloadAttempt() === reason || !writeReloadAttempt(reason)) {
      return;
    }
    this.reloadPage(routerUrl);
  }
}

function readVersion(body: unknown): string | null {
  return typeof body === 'object' &&
    body !== null &&
    'version' in body &&
    typeof body.version === 'string'
    ? body.version
    : null;
}

function isChunkLoadFailure(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_FAILURE.test(error.message);
}

function readReloadAttempt(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
  } catch (error) {
    console.warn('Reading the last app reload failed', error);
    return null;
  }
}

/** False when the attempt cannot be recorded: reloading then risks a loop. */
function writeReloadAttempt(reason: string): boolean {
  try {
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, reason);
    return true;
  } catch (error) {
    console.warn('Recording an app reload failed; not reloading', error);
    return false;
  }
}
